// cSpell: words mapboxgl saleticket mapbox gmap
import { Spot } from "../global-commons/type";
import { DATA_BLOCK } from "./dataBlockUtil";
import { getSaleTicketWithSpots } from "./searchApiUtil";
import { Languages } from "../global-commons/i18n/base";
import {
  MOBILE_WIDTH,
  MOBILE_DRAWER_INITIAL_TOP,
  UPPER_NAVBAR_HEIGHT,
  MIDDLE_NAVBAR_HEIGHT,
  createTicketCard,
} from "./gmapCommon";

/** スポット詳細ドロワーにおいて、販売チケットが検索されたか */
let isSaleTicketSearched: boolean = false;

/** スポット詳細ドロワーを開く */
export async function openSpotDetailDrawer(spot: Spot) {
  const detailDrawer = document.getElementById("spot-detail-drawer");
  if (!detailDrawer) {
    throw new Error("spot-detail-drawer not found");
  }

  // ドロワーが開いている状態であることを示すために
  // インラインスタイルにtop: 0px;をセットする
  detailDrawer.style.top = "0px";

  // スクロール可能なコンテンツのスクロール位置を初期化する
  const spotDetailDrawerContent = document.getElementById(
    "spot-detail-drawer-content",
  );
  if (!spotDetailDrawerContent) {
    throw new Error("spot-detail-drawer-content not found");
  }
  spotDetailDrawerContent.scrollTop = 0;

  document.getElementById("spot-detail-drawer-title")!.textContent =
    spot.subject;

  const spotDetailDrawerTags = document.getElementById(
    "spot-detail-drawer-tags",
  );
  if (!spotDetailDrawerTags) {
    throw new Error("spot-detail-drawer-tags not found");
  }

  spotDetailDrawerTags.innerHTML = "";

  spot.tags.forEach((tag) => {
    const tagElement = document.createElement("span");
    tagElement.classList.add("filter-tag");
    tagElement.innerText = tag.tag_nm.replace(/_/g, "\u00A0");
    spotDetailDrawerTags.appendChild(tagElement);
  });

  document.getElementById("spot-detail-drawer-description")!.innerText =
    spot.description;
  const imageContainer = document.getElementById(
    "spot-detail-drawer-image-container",
  );
  if (!imageContainer) {
    throw new Error("image container not found");
  }
  const spotDetailDrawerIndicators = document.getElementById(
    "spot-detail-drawer-indicators",
  );
  if (!spotDetailDrawerIndicators) {
    throw new Error("spot-detail-drawer-indicators not found");
  }
  imageContainer.innerHTML = "";
  spotDetailDrawerIndicators.innerHTML = "";

  // カルーセル要素を取得し、有効な画像が無い場合は非表示にする
  const spotDetailCarousel = document.getElementById("spotDetailCarousel");
  if (!spotDetailCarousel) {
    throw new Error("spotDetailCarousel not found");
  }

  // URLが空やnull/undefinedの画像をフィルタして有効な画像のみを対象とする
  const validImages = (spot.images || []).filter(
    (image) => image.url !== "" && image.url != null,
  );

  if (validImages.length === 0) {
    spotDetailCarousel.style.display = "none";
  } else {
    spotDetailCarousel.style.display = "";

    for (const i in validImages) {
      // カルーセルに画像を追加する
      const div = document.createElement("div");
      div.classList.add("carousel-item");
      if (i === "0") {
        div.classList.add("active");
      }

      const img = document.createElement("img");
      img.src = validImages[i].url;
      img.alt = validImages[i].desc ?? "";
      img.classList.add("d-block", "w-100", "drawer-image");

      div.appendChild(img);
      imageContainer.appendChild(div);

      // カルーセルのインジケータを追加する
      const indicator = document.createElement("button");
      indicator.type = "button";
      indicator.setAttribute("data-bs-target", "#spotDetailCarousel");
      indicator.setAttribute("data-bs-slide-to", i);
      indicator.setAttribute("aria-label", `Slide ${parseInt(i) + 1}`);
      indicator.classList.add("override-background-color");
      if (i === "0") {
        indicator.classList.add("active");
        indicator.setAttribute("aria-current", "true");
      }
      spotDetailDrawerIndicators.appendChild(indicator);
    }
  }

  // 販売チケット一覧を削除する
  const saleTicketListContainer = document.getElementById(
    "spot-detail-drawer-sale-ticket-list-container",
  );
  if (!saleTicketListContainer) {
    throw new Error("spot-detail-drawer-sale-ticket-list-container not found");
  }
  saleTicketListContainer.innerHTML = "";

  // 販売チケット一覧の読み込みボタンを表示する
  const loadButton = document.getElementById(
    "spot-detail-drawer-load-sale-ticket-button",
  );
  if (!loadButton) {
    throw new Error("spot-detail-drawer-load-sale-ticket-button not found");
  }

  // 販売チケットはありませんメッセージを非表示にする
  const noTicketAvailableMessage = document.getElementById(
    "spot-detail-drawer-no-ticket-available",
  );
  if (!noTicketAvailableMessage) {
    throw new Error("spot-detail-drawer-no-ticket-available not found");
  }

  // 設定値の取得
  const enableSearchAvailableTickets =
    DATA_BLOCK.getEnableSearchAvailableTickets();

  // spot.ticketsが空ならば、紐づけられる販売チケットは存在しない
  if (spot.tickets.length === 0) {
    loadButton.style.display = "none";
    noTicketAvailableMessage.style.display = "block";
  } else {
    // チケットが存在する場合、設定値に応じてボタンの表示を切り替える
    if (enableSearchAvailableTickets) {
      loadButton.style.display = "block";
      noTicketAvailableMessage.style.display = "none";
    } else {
      // falseの場合はボタンを非表示にする（メッセージは読み込み完了まで非表示）
      loadButton.style.display = "none";
      noTicketAvailableMessage.style.display = "none";
    }
  }

  const spotDetailDrawerOperationalHours = document.getElementById(
    "spot-detail-drawer-operational-hours",
  );

  if (!spotDetailDrawerOperationalHours) {
    throw new Error("spotDetailDrawerOperationalHours not found");
  }
  spotDetailDrawerOperationalHours.innerText = spot.operationalHours;

  const spotDetailDrawerAddress = document.getElementById(
    "spot-detail-drawer-address",
  );
  if (!spotDetailDrawerAddress) {
    throw new Error("spotDetailDrawerAddress not found");
  }
  spotDetailDrawerAddress.innerText = spot.address;

  const spotDetailDrawerPhoneNumber = document.getElementById(
    "spot-detail-drawer-phone-number",
  );
  if (!spotDetailDrawerPhoneNumber) {
    throw new Error("spotDetailDrawerPhoneNumber not found");
  }
  spotDetailDrawerPhoneNumber.innerText = spot.phoneNumber;

  const spotDetailDrawerLinks = document.getElementById(
    "spot-detail-drawer-links",
  );
  if (!spotDetailDrawerLinks) {
    throw new Error("spotDetailDrawerLinks not found");
  }

  spotDetailDrawerLinks.innerHTML = "";

  const webSiteCount = Math.min(
    spot.webSiteName.length,
    spot.webSiteUrl.length,
  );
  for (let i = 0; i < webSiteCount; i++) {
    const link = document.createElement("a");
    link.href = spot.webSiteUrl[i];
    link.innerText = spot.webSiteName[i];
    link.target = "_blank";
    spotDetailDrawerLinks.appendChild(link);
    // 最後以外は改行を追加
    if (i < webSiteCount - 1) {
      spotDetailDrawerLinks.appendChild(document.createElement("br"));
    }
  }

  // 戻るボタンを押してスポット詳細ドロワーを開いた場合
  //　最初から販売チケットが検索される
  if (
    (isSaleTicketSearched || !enableSearchAvailableTickets) &&
    spot.tickets.length > 0
  ) {
    await loadAvailableSaleTickets();
  }

  isSaleTicketSearched = false;

  locateSpotDetailDrawer();
}

/** スポット詳細ドロワーの位置を調整する */
export function locateSpotDetailDrawer() {
  const detailDrawer = document.getElementById("spot-detail-drawer");
  if (!detailDrawer) {
    throw new Error("spot-detail-drawer not found");
  }
  const spotDetailHandle = document.getElementById("spot-detail-handle");
  if (!spotDetailHandle) {
    throw new Error("spot-detail-handle not found");
  }
  // ドロワーの初期位置をウィンドウ幅に応じて設定する
  const windowWidth = window.innerWidth;
  if (windowWidth >= MOBILE_WIDTH) {
    // PC画面では、navbarの下端にドロワーを配置する
    // ただし、ドロワーが開いている時のみ(=インラインスタイルでtopが設定されている時)
    if (detailDrawer.style.top !== "") {
      detailDrawer.style.top = `${
        UPPER_NAVBAR_HEIGHT + MIDDLE_NAVBAR_HEIGHT
      }px`;
    }
    // PC画面では、ドロワーのハンドルを非表示にする
    spotDetailHandle.style.display = "none";
    // ドロワーの幅を固定値に設定する
    detailDrawer.style.width = "500px";
  } else {
    // モバイル画面では、ドロワーの初期位置をnavbarの下端に設定する
    if (detailDrawer.style.top !== "") {
      detailDrawer.style.top = `${MOBILE_DRAWER_INITIAL_TOP}px`;
    }
    // モバイル画面では、ドロワーのハンドルを表示する
    spotDetailHandle.style.display = "block";
    // モバイル画面では、ドロワーの幅をウィンドウ幅に設定する
    detailDrawer.style.width = `${windowWidth}px`;
  }
}

/** スポット詳細ドロワーを非表示にする */
export function closeSpotDetailDrawer() {
  const detailDrawer = document.getElementById("spot-detail-drawer");
  if (!detailDrawer) {
    throw new Error("spot-detail-drawer not found");
  }

  detailDrawer.style.top = "";
}

/** 現在開いているスポットで使用可能な販売チケットをAPIコールして調べて、
 * その結果をスポット詳細ドロワーに表示する
 */
export async function loadAvailableSaleTickets() {
  const params = new URLSearchParams(document.location.search);
  const spot = params.get("spot");
  if (!spot) {
    throw new Error(
      "loadAvailableSaleTickets can only be called in singleSpot mode",
    );
  }
  const spotId = parseInt(spot, 10);

  const saleTicketListContainer = document.getElementById(
    "spot-detail-drawer-sale-ticket-list-container",
  );
  if (!saleTicketListContainer) {
    throw new Error("spot-detail-drawer-sale-ticket-list-container not found");
  }
  const loadButton = document.getElementById(
    "spot-detail-drawer-load-sale-ticket-button",
  );
  if (!loadButton) {
    throw new Error("spot-detail-drawer-load-sale-ticket-button not found");
  }
  const loadingSpinner = document.getElementById(
    "spot-detail-drawer-sale-ticket-loading-spinner",
  );
  if (!loadingSpinner) {
    throw new Error("spot-detail-drawer-sale-ticket-loading-spinner not found");
  }
  loadingSpinner.style.display = "block";
  loadButton.style.display = "none";
  const location = window.location;
  const lang = location.pathname.split("/")[1];
  try {
    saleTicketListContainer.innerHTML = "";
    const spotWithSaleTicket = await getSaleTicketWithSpots(
      spotId,
      DATA_BLOCK.getEnv().SEARCH_API_URL,
      lang as Languages,
    );
    const targetSaleTickets = spotWithSaleTicket
      .map((saleTicket) => saleTicket.saleTickets)
      .flat();

    // 現在販売していないチケットをフィルタする
    const now = new Date();
    const filteredSaleTickets = targetSaleTickets.filter((saleTicket) => {
      const saleStartAt = new Date(saleTicket.saleStartAt);
      const saleEndAt = new Date(saleTicket.saleEndAt);
      return saleStartAt <= now && now <= saleEndAt;
    });
    filteredSaleTickets.forEach((targetSaleTicket) => {
      const card = createTicketCard(targetSaleTicket, DATA_BLOCK.getI18n());
      card.addEventListener("click", () => {
        window.dispatchEvent(
          new CustomEvent("changeModeToSaleTicket", {
            detail: { id: targetSaleTicket.topics_id },
          }),
        );
      });
      saleTicketListContainer.appendChild(card);
    });
    const noTicketAvailableMessage = document.getElementById(
      "spot-detail-drawer-no-ticket-available",
    );
    // 販売チケットがない場合はメッセージを表示する
    if (filteredSaleTickets.length === 0) {
      if (noTicketAvailableMessage) {
        noTicketAvailableMessage.style.display = "block";
      }
    } else if (noTicketAvailableMessage) {
      noTicketAvailableMessage.style.display = "none";
    }
  } finally {
    loadingSpinner.style.display = "none";
    isSaleTicketSearched = false;
  }
}

/** Set the isSaleTicketSearched flag */
export function setIsSaleTicketSearched(value: boolean) {
  isSaleTicketSearched = value;
}
